import { AppShell, BrandLogo } from '@/shared/ui';
import { LocationWorkspaceSwitcher, useAuthStore, useWorkspaceLabel } from '@/shared/platform';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getNavKeyFromPath, NAV_PATHS, OPS_NAV_KEYS, SIDEBAR_NAV } from '@/app/config/nav';
import { buildPageBreadcrumb } from '@/app/layouts/pageBreadcrumb';
import { PageTopBarProvider, usePageTopBarContext } from '@/app/layouts/pageTopBar';
import { useWaitingCount } from '@/features/claims-review/store';
import { RcmSessionBadge } from '@/features/session/components/RcmSessionBadge';
import { useAppealsWaitingCount } from '@/features/appeals/store';
import { useToSendCount } from '@/features/preauths/store';
import { usePostingCount } from '@/features/posting/store';
import { useHitlCount } from '@/features/human-in-the-loop/store';
import { useArLateCount } from '@/features/insurance-ar/store';
import { useDocCenterCount } from '@/features/document-center/store';
import { GlobalSearch } from '@/components/search/GlobalSearch';

function initialsFromName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0];
  if (!first) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1];
  return `${first[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

function AppLayoutShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const active = getNavKeyFromPath(location.pathname);
  const workspaceLabel = useWorkspaceLabel();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const {
    breadcrumb: breadcrumbOverride,
    actionsRef,
    actionsTick,
    hideTopBar,
  } = usePageTopBarContext();

  const user = useAuthStore((s) => s.user);
  const organization = useAuthStore((s) => s.organization);
  const logout = useAuthStore((s) => s.logout);

  // Claims waiting for review — live count on the sidebar (CLAIMS spec §1.2).
  const claimsWaiting = useWaitingCount();
  const deniedWaiting = useAppealsWaitingCount();
  const preauthsWaiting = useToSendCount();
  const postingWaiting = usePostingCount();
  const hitlWaiting = useHitlCount();
  const arLate = useArLateCount();
  const docCenter = useDocCenterCount();
  const navItems = SIDEBAR_NAV.map((item) => {
    if (item.key === 'claims') return { ...item, badge: claimsWaiting };
    if (item.key === 'denied') return { ...item, count: deniedWaiting };
    if (item.key === 'pre-auths') return { ...item, count: preauthsWaiting };
    if (item.key === 'posting') return { ...item, count: postingWaiting };
    if (item.key === 'human-in-the-loop') return { ...item, badge: hitlWaiting };
    if (item.key === 'ar') return { ...item, count: arLate };
    if (item.key === 'document-center') return { ...item, count: docCenter };
    return item;
  }).map((item) => {
    // Sidebar rows are real links (D202): open in a new tab, URL on hover.
    const href = NAV_PATHS[item.key];
    return href ? { ...item, href } : item;
  });

  const displayName = user
    ? user.name.trim() || (user.email.split('@')[0] ?? 'Account')
    : 'Account';
  const roleLabel =
    user?.role && typeof user.role === 'object' && user.role.name ? user.role.name : 'RCM';
  // Sidebar footer shows the person and their organisation (rcm-02 fix-on-staging), both
  // already present on the real session — no mock/second copy is introduced here.
  const footerSubtitle = organization?.name ? `${roleLabel} · ${organization.name}` : roleLabel;

  const breadcrumb = breadcrumbOverride ?? buildPageBreadcrumb(location.pathname, workspaceLabel);
  const topBarActions = actionsTick >= 0 ? actionsRef.current : null;
  const fillContent =
    hideTopBar ||
    active === 'worklist' ||
    active === 'settings' ||
    active === 'calendar' ||
    active === 'patients' ||
    active === 'claims' ||
    active === 'sent-claims' ||
    active === 'denied' ||
    active === 'document-center' ||
    (OPS_NAV_KEYS as readonly string[]).includes(active);

  return (
    <AppShell
      fill={fillContent}
      sidebarCollapsed={sidebarCollapsed}
      onSidebarCollapsedChange={setSidebarCollapsed}
      sidebar={{
        brand: {
          name: 'Elva',
          badge: 'RCM',
          logo: <BrandLogo src="/logo.svg" alt="Elva" />,
        },
        workspaceSlot: (
          <>
            <LocationWorkspaceSwitcher collapsed={sidebarCollapsed} coreIds={false} />
            {/* What the RCM backend says about this session. The one thing
                in the frame that is not fixture data, so it is also where a
                wiring problem shows up first. */}
            <RcmSessionBadge collapsed={sidebarCollapsed} />
          </>
        ),
        // dev's Sidebar has no `searchSlot`; `productSlot` renders in the
        // same place — below the brand, above the navigation.
        productSlot: <GlobalSearch collapsed={sidebarCollapsed} />,
        nav: navItems,
        active,
        onNav: (key) => {
          const path = NAV_PATHS[key];
          if (path) void navigate(path);
        },
        // Visual redesign direction D: the account lives in the sidebar footer.
        user: {
          name: displayName,
          role: footerSubtitle,
          initials: initialsFromName(displayName),
          onLogout: () => {
            void logout().catch(() => undefined);
          },
        },
      }}
      topbar={{
        breadcrumb,
        titleGradient: true,
        actions: topBarActions,
      }}
    >
      <Outlet />
    </AppShell>
  );
}

export function AppLayout() {
  return (
    <PageTopBarProvider>
      <AppLayoutShell />
    </PageTopBarProvider>
  );
}
